import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { L10nTranslationService } from 'angular-l10n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as SessionActions from '@wonder-core-ng/session-store/session.actions';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { BI_CORE_CONFIG } from '../config';
import { AlertApplyResult, AlertProposal } from './alert-proposal.types';
import { AssistantPersona, ChatStreamEvent, ComposerAnswers, ConversationSummary, GetConversationResponse, GetModelsResponse, ProposalRevisionDraft, RouteHistoryTurn, RouteStreamEvent, SendChatMessageResponse, ThinkingLevel } from './ai-chat.types';

/**
 * In root, not only in AIStoreModule: the global assistant (a root service in the core's header
 * bar) uses it on every screen of every host, including the hosts' AppComponent specs that never
 * import the AI store. It needs nothing beyond HttpClient and BI_CORE_CONFIG.
 */
@Injectable({ providedIn: 'root' })
export class AIApiService {
  private httpClient = inject(HttpClient);
  private readonly config = inject(BI_CORE_CONFIG);
  // Optional: hosts' specs that never import the session store still build this service.
  private readonly store = inject(Store, { optional: true });
  private readonly translation = inject(L10nTranslationService, { optional: true });

  private readonly route = `${this.config.apiBaseUrl}/ai`;

  /** UI locale sent to the assistant so it answers in the user's language. */
  private currentLocale(): string | undefined {
    return this.translation?.getLocale()?.language || undefined;
  }

  sendChatMessage(message: string, conversationId: string | null, tenant: id, model?: string, thinkingLevel?: ThinkingLevel): Observable<SendChatMessageResponse> {
    return this.httpClient.post<SendChatMessageResponse>(
      `${this.route}/chat`,
      { message, conversationId: conversationId ?? undefined, model: model ?? undefined, thinkingLevel: thinkingLevel ?? undefined },
      { headers: { 'x-tenant-id': `${tenant}` } },
    );
  }

  /** Scores a finished session from 1 to 5; only its author may. */
  rateSession(conversationId: string, rating: number, tenant: id): Observable<void> {
    return this.httpClient.post<void>(
      `${this.route}/chat/${conversationId}/rating`,
      { rating },
      { headers: { 'x-tenant-id': `${tenant}` } },
    );
  }

  /**
   * Runs a message of the global assistant through the orchestrator (WB-154/155), streamed: which
   * agent is working, the reply as it is written, and last the whole result, which answers in place
   * (with the guide agent's links) or hands the request to a builder agent.
   */
  routeMessageStream(tenant: id, message: string, history: RouteHistoryTurn[], dashboardDraft?: ProposalRevisionDraft, composerAnswers?: ComposerAnswers, alertAnswers?: ComposerAnswers): Observable<RouteStreamEvent> {
    // WB-179: the proposal open in the editor travels only when there is one, so the assistant can change it.
    // WB-177 phase 5: the answers to the composer's questionnaire, with the request they answer.
    // WB-222: the answers to the alerts agent's questionnaire, same shape as the composer's.
    const body = { message, history, locale: this.currentLocale(), ...(dashboardDraft ? { dashboardDraft } : {}), ...(composerAnswers ? { composerAnswers } : {}), ...(alertAnswers ? { alertAnswers } : {}) };
    return this.postEventStream<RouteStreamEvent>(`${this.route}/route/stream`, body, tenant);
  }

  /**
   * Saves the alert the user confirmed in the chat (WB-222): the server re-validates it and creates
   * the condition, the event and the actions in one go. Nothing is saved before this call.
   */
  applyAlert(tenant: id, proposal: AlertProposal): Observable<AlertApplyResult> {
    return this.httpClient.post<AlertApplyResult>(`${this.route}/alerts/apply`, { proposal }, { headers: { 'x-tenant-id': `${tenant}` } });
  }

  /** Models the user may switch the assistant to. Requires the AI_MODEL_SELECT permission. */
  getModels(tenant: id): Observable<GetModelsResponse> {
    return this.httpClient.get<GetModelsResponse>(
      `${this.route}/models`,
      { headers: { 'x-tenant-id': `${tenant}` } },
    );
  }

  sendChatMessageStream(
    message: string,
    conversationId: string | null,
    tenant: id,
    currentQueryConfig?: Record<string, unknown>,
    currentChartConfig?: Record<string, unknown>,
    context?: 'query' | 'chart',
    model?: string,
    thinkingLevel?: ThinkingLevel,
  ): Observable<ChatStreamEvent> {
    const body = {
      message,
      conversationId: conversationId ?? undefined,
      currentQueryConfig: currentQueryConfig ?? undefined,
      currentChartConfig: currentChartConfig ?? undefined,
      context: context ?? undefined,
      model: model ?? undefined,
      thinkingLevel: thinkingLevel ?? undefined,
      locale: this.currentLocale(),
    };
    return this.postEventStream<ChatStreamEvent>(`${this.route}/chat/stream`, body, tenant);
  }

  /**
   * POSTs `body` and emits each Server-Sent Event of the answer as parsed JSON. Unsubscribing aborts
   * the request, which the server sees as a disconnect.
   */
  private postEventStream<T>(url: string, payload: unknown, tenant: id): Observable<T> {
    const body = JSON.stringify(payload);
    const headers = { 'Content-Type': 'application/json', 'x-tenant-id': `${tenant}` };

    return new Observable<T>(subscriber => {
      const controller = new AbortController();

      // Opens the SSE stream and pumps events to the subscriber. Because this is a
      // raw fetch (SSE can't go through the HttpClient auth interceptor), it returns
      // an http-error result on non-2xx so the caller can refresh the token and retry.
      const openStream = async (): Promise<{ kind: 'done' } | { kind: 'http-error'; status: number; text: string }> => {
        const response = await fetch(url, { method: 'POST', headers, body, credentials: 'include', signal: controller.signal });
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          return { kind: 'http-error', status: response.status, text };
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let remainder = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = remainder + decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          remainder = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            try {
              subscriber.next(JSON.parse(data) as T);
            } catch {
              // skip malformed lines
            }
          }
        }
        return { kind: 'done' };
      };

      (async () => {
        try {
          let result = await openStream();
          // Access token expired: the interceptor never sees this SSE call, so refresh
          // the cookie session here and retry the stream once (mirrors the interceptor).
          if (result.kind === 'http-error' && result.status === 401) {
            const refreshed = await this.refreshSession(tenant, controller.signal);
            if (refreshed) result = await openStream();
          }
          if (result.kind === 'http-error') {
            subscriber.error(new Error(`HTTP ${result.status}: ${result.text}`));
            return;
          }
          subscriber.complete();
        } catch (err) {
          if ((err as Error)?.name !== 'AbortError') subscriber.error(err);
        }
      })();

      return () => controller.abort();
    });
  }

  /**
   * Refresh the (cookie-based) session by POSTing to the same refresh endpoint the
   * auth interceptor uses. Returns true if the server issued fresh cookies. Used to
   * recover from a 401 on the SSE stream, which bypasses the HttpClient interceptor.
   *
   * The `x-tenant-id` header is required by the tenant middleware on every request;
   * the interceptor gets it for free by cloning the original request, so we must set
   * it explicitly here or the refresh is rejected with 400 before the handler runs.
   */
  private async refreshSession(tenant: id, signal: AbortSignal): Promise<boolean> {
    try {
      const response = await fetch(`${this.config.apiBaseUrl}/auth/refreshToken`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-tenant-id': `${tenant}` },
        signal,
      });
      if (response.ok) {
        // Same as the interceptor: the refresh answers with the current session; a server that
        // predates that answers `{ message }`, which must not overwrite it.
        const session = await response.json().catch(() => null);
        if (session?.user && Array.isArray(session.permissions)) {
          this.store?.dispatch(SessionActions.sessionRefreshed({ session }));
        }
        return true;
      }
      // Only a refused refresh ends the session; 0 or 5xx (network, deployment) leave it as it is.
      if (response.status === 401 || response.status === 403) this.store?.dispatch(SessionActions.removeSession());
      return false;
    } catch {
      return false;
    }
  }

  generateChartConfig(
    tenant: id,
    description: string,
    queryIds: string[],
  ): Observable<{ chartConfig: Record<string, unknown>; queryIds: string[] }> {
    return this.httpClient.post<{ chartConfig: Record<string, unknown>; queryIds: string[] }>(
      `${this.route}/generate-chart-config`,
      { description, queryIds },
      { headers: { 'x-tenant-id': `${tenant}` } },
    );
  }

  /**
   * Ready-made phrases for the welcome of the assistant the panel is showing: the builders' come from
   * the tenant's config, the guide's from what this user can open, in the interface's language.
   */
  getSuggestions(tenant: id, agent: AssistantPersona = 'builder'): Observable<{ suggestions: string[] }> {
    const locale = this.currentLocale();
    return this.httpClient.get<{ suggestions: string[] }>(
      `${this.route}/suggestions`,
      { headers: { 'x-tenant-id': `${tenant}` }, params: locale ? { agent, locale } : { agent } },
    );
  }

  clearConversation(conversationId: string, tenant: id): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.route}/chat/${conversationId}`,
      { headers: { 'x-tenant-id': `${tenant}` } },
    );
  }


  /** List the logged-in user's past conversations (history sidebar). */
  listConversations(tenant: id): Observable<ConversationSummary[]> {
    return this.httpClient.get<ConversationSummary[]>(
      `${this.route}/conversations`,
      { headers: { 'x-tenant-id': `${tenant}` } },
    );
  }

  /** Load a past conversation to resume it. */
  getConversation(conversationId: string, tenant: id): Observable<GetConversationResponse> {
    return this.httpClient.get<GetConversationResponse>(
      `${this.route}/chat/${conversationId}`,
      { headers: { 'x-tenant-id': `${tenant}` } },
    );
  }
}
